/**
 * CR098 AC3-AC6: Deal Card Draft Persistence Hook
 * 
 * Provides per-deal-type draft storage with localStorage persistence.
 * Drafts survive page refresh and are isolated per deal type.
 * 
 * Key features:
 * - Draft persistence across browser refresh (AC3)
 * - Per-deal-type isolation (AC6)
 * - Discard resets to last saved (AC4)
 * - Save clears drafts and invalidates cache (AC5)
 * - Storage key includes domain for collision avoidance
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import type { CardStyling } from "@/components/admin/command-center/CardStylingPanel";
import type { HeaderComposition } from "@/components/admin/command-center/HeaderCompositionPanel";
import type { ExtendedSectionToggles } from "@/components/admin/command-center/SectionTogglesPanel";
import type { RibbonDraft } from "@/components/admin/command-center/RibbonSettingsPanel";
import type { CardTemplateJson } from "@/providers/DealCardConfigProvider";
import type { ComponentsConfig } from "@/components/admin/command-center/DealCardsTab";
// CR319: Import promo badges and details content config types
import type { PromoBadgesConfig } from "@/components/admin/deal-cards/PromoBadgesAccordion";
import type { DetailsContentConfig } from "@/components/admin/deal-cards/DetailsContentAccordion";

// ================================================================
// TYPES
// ================================================================

export interface DealTypeDraft {
  ribbon: RibbonDraft;
  header: HeaderComposition;
  sections: ExtendedSectionToggles;
  labels: CardTemplateJson["labels"];
  styling: CardStyling;
  sectionOrder: string[];
  themePreset: "light" | "dark" | "custom";
  // CR323: Surface-specific block toggles for V2
  surfaceBlockToggles?: { primary: Record<string, boolean>; details: Record<string, boolean> };
  // CR312: Surface-specific block order for V2 reordering
  surfaceBlockOrder?: { primary: string[]; details: string[] };
  // CR316: Components config (typography for Header, Ribbon, Starburst)
  components?: ComponentsConfig;
  // CR319: Promo badges config
  promoBadges?: PromoBadgesConfig;
  // CR319: Details content config
  detailsContent?: DetailsContentConfig;
  savedAt: string; // ISO timestamp when draft was created/updated
}

export interface AllDrafts {
  [dealTypeKey: string]: DealTypeDraft;
}

// ================================================================
// CONSTANTS
// ================================================================

/**
 * Storage key format: command-center-drafts_v{version}_{domain}
 * Domain = "deal-cards" to avoid collision with other Command Center drafts
 * User ID is NOT included because localStorage is already per-origin and
 * deal card config is admin-only (auth required to access this page)
 */
const STORAGE_KEY_PREFIX = "command-center-drafts";
const STORAGE_DOMAIN = "deal-cards";
const STORAGE_VERSION = 2; // Bumped for new key format

function getStorageKey(): string {
  return `${STORAGE_KEY_PREFIX}_v${STORAGE_VERSION}_${STORAGE_DOMAIN}`;
}

interface StorageSchema {
  version: number;
  domain: string;
  drafts: AllDrafts;
  lastModified: string;
}

// ================================================================
// STORAGE HELPERS
// ================================================================

function loadFromStorage(): AllDrafts {
  try {
    const key = getStorageKey();
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    
    const parsed: StorageSchema = JSON.parse(raw);
    
    // Version + domain check for safety
    if (parsed.version !== STORAGE_VERSION || parsed.domain !== STORAGE_DOMAIN) {
      console.warn("[useDealCardDrafts] Storage schema mismatch, clearing drafts");
      localStorage.removeItem(key);
      return {};
    }
    
    return parsed.drafts || {};
  } catch (err) {
    console.error("[useDealCardDrafts] Failed to load drafts from localStorage:", err);
    return {};
  }
}

function saveToStorage(drafts: AllDrafts): void {
  try {
    const schema: StorageSchema = {
      version: STORAGE_VERSION,
      domain: STORAGE_DOMAIN,
      drafts,
      lastModified: new Date().toISOString(),
    };
    localStorage.setItem(getStorageKey(), JSON.stringify(schema));
  } catch (err) {
    console.error("[useDealCardDrafts] Failed to save drafts to localStorage:", err);
  }
}

function clearDraftForType(drafts: AllDrafts, dealTypeKey: string): AllDrafts {
  const next = { ...drafts };
  delete next[dealTypeKey];
  return next;
}

/**
 * Clear ALL drafts from localStorage (global reset)
 * Does NOT affect database values - only local drafts
 */
export function clearAllLocalDrafts(): void {
  try {
    localStorage.removeItem(getStorageKey());
    console.log("[useDealCardDrafts] Cleared all local drafts");
  } catch (err) {
    console.error("[useDealCardDrafts] Failed to clear local drafts:", err);
  }
}

// ================================================================
// HOOK
// ================================================================

export function useDealCardDrafts() {
  // CR111D: Avoid localStorage in useState initializer (SSR/hydration safety)
  const [drafts, setDrafts] = useState<AllDrafts>({});
  const [initialized, setInitialized] = useState(false);
  
  // CR111D: Load from localStorage only in useEffect (client-side)
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        const loaded = loadFromStorage();
        setDrafts(loaded);
      } catch (err) {
        console.error("[useDealCardDrafts] Error loading drafts:", err);
      }
    }
    setInitialized(true);
  }, []);
  
  // Persist to localStorage whenever drafts change (after initial load)
  useEffect(() => {
    if (initialized) {
      saveToStorage(drafts);
    }
  }, [drafts, initialized]);
  
  /**
   * Get draft for a specific deal type (or null if no draft exists)
   */
  const getDraft = useCallback((dealTypeKey: string): DealTypeDraft | null => {
    return drafts[dealTypeKey] || null;
  }, [drafts]);
  
  /**
   * Check if a draft exists for a deal type
   */
  const hasDraft = useCallback((dealTypeKey: string): boolean => {
    return !!drafts[dealTypeKey];
  }, [drafts]);
  
  /**
   * Set/update draft for a deal type
   */
  const setDraft = useCallback((dealTypeKey: string, draft: DealTypeDraft): void => {
    setDrafts(prev => ({
      ...prev,
      [dealTypeKey]: draft,
    }));
  }, []);
  
  /**
   * Update partial draft fields for a deal type
   */
  const updateDraft = useCallback((dealTypeKey: string, updates: Partial<DealTypeDraft>): void => {
    setDrafts(prev => {
      const existing = prev[dealTypeKey];
      if (!existing) {
        console.warn(`[useDealCardDrafts] No draft exists for ${dealTypeKey}, cannot update`);
        return prev;
      }
      return {
        ...prev,
        [dealTypeKey]: { ...existing, ...updates },
      };
    });
  }, []);
  
  /**
   * Discard draft for a deal type (removes from storage)
   */
  const discardDraft = useCallback((dealTypeKey: string): void => {
    setDrafts(prev => clearDraftForType(prev, dealTypeKey));
  }, []);
  
  /**
   * Discard all drafts (used after bulk save or reset)
   */
  const discardAllDrafts = useCallback((): void => {
    setDrafts({});
  }, []);
  
  /**
   * Get all deal types that have drafts
   */
  const getDraftedTypes = useCallback((): string[] => {
    return Object.keys(drafts);
  }, [drafts]);
  
  /**
   * Check if any drafts exist
   */
  const hasAnyDrafts = useMemo((): boolean => {
    return Object.keys(drafts).length > 0;
  }, [drafts]);
  
  return {
    drafts,
    getDraft,
    hasDraft,
    setDraft,
    updateDraft,
    discardDraft,
    discardAllDrafts,
    getDraftedTypes,
    hasAnyDrafts,
  };
}

// ================================================================
// UTILITY: Build effective config from saved + draft
// ================================================================

export interface EffectiveConfig {
  ribbon: RibbonDraft;
  header: HeaderComposition;
  sections: ExtendedSectionToggles;
  labels: CardTemplateJson["labels"];
  styling: CardStyling;
  sectionOrder: string[];
  themePreset: "light" | "dark" | "custom";
}

/**
 * Merge saved config with draft to get effective config for preview.
 * Draft values override saved values.
 */
export function buildEffectiveConfig(
  saved: Partial<EffectiveConfig>,
  draft: Partial<EffectiveConfig> | null
): EffectiveConfig {
  if (!draft) {
    return saved as EffectiveConfig;
  }
  
  return {
    ribbon: draft.ribbon ?? saved.ribbon!,
    header: draft.header ?? saved.header!,
    sections: draft.sections ?? saved.sections!,
    labels: draft.labels ?? saved.labels!,
    styling: draft.styling ?? saved.styling!,
    sectionOrder: draft.sectionOrder ?? saved.sectionOrder!,
    themePreset: draft.themePreset ?? saved.themePreset!,
  };
}
